import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { translateErrorCode } from '../../i18n';

/**
 * The states every component must implement (section 9.3): loading, empty,
 * error, and access-denied. Providing them centrally is what lets each renderer
 * concentrate on the one state that is actually interesting -- and stops the
 * failure modes drifting apart between components.
 */

export function LoadingState({ label, rows = 3 }: { label?: string; rows?: number }) {
  const { t } = useTranslation();
  return (
    <div className="skeleton-stack" role="status" aria-busy="true" aria-live="polite">
      {/*
        The visible skeleton is decorative; the label is what a screen reader
        announces. Announcing every shimmering rectangle would be noise
        (section 18: dynamic updates must not be disruptive).
      */}
      <span className="visually-hidden">{label ?? t('state.loading')}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="skeleton"
          aria-hidden="true"
          style={{ height: index === 0 ? 22 : 14, width: index === 0 ? '55%' : '100%' }}
        />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title?: string;
  body?: ReactNode;
  /** A way out of the empty state, such as a button to create the missing thing. */
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="state">
      <span className="state-title">{title ?? t('state.emptyTitle')}</span>
      {body ? <span>{body}</span> : null}
      {action}
    </div>
  );
}

export function PermissionDeniedState() {
  const { t } = useTranslation();
  return (
    <div className="state">
      <span className="state-title">{t('state.permissionTitle')}</span>
      <span>{t('state.permissionBody')}</span>
    </div>
  );
}

/**
 * Errors show the translated message for the code plus the correlation
 * identifier, so a user reporting a problem can quote the exact request the
 * operator needs to find in the logs (section 10.2).
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();

  const apiError = error instanceof ApiError ? error : null;
  const message = apiError
    ? translateErrorCode(apiError.code, apiError.params)
    : translateErrorCode('internal.error');

  return (
    <div className="state state--error" role="alert">
      <span className="state-title">{t('state.errorTitle')}</span>
      <span>{message}</span>
      {apiError?.correlationId ? (
        <span className="state-correlation">
          {t('state.correlation', { id: apiError.correlationId })}
        </span>
      ) : null}
      {onRetry ? (
        <button type="button" className="button" onClick={onRetry}>
          {t('state.retry')}
        </button>
      ) : null}
    </div>
  );
}
